import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextareaAutosize,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  LinearProgress,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  DirectionsCar as CarIcon,
  Build as BuildIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Videocam as VideocamIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  CloudDone as CloudDoneIcon,
  Sync as SyncIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import api from '../services/api';
import ImageAnnotator from './ImageAnnotator';
import CompletionConfirmationDialog from './CompletionConfirmationDialog';

// Memoized component for general notes to prevent re-renders
const GeneralNotesField = memo(({ value, onChange }) => (
  <TextField
    fullWidth
    multiline
    rows={4}
    placeholder="Add any general notes or recommendations..."
    value={value}
    onChange={onChange}
    variant="outlined"
  />
));

const VehicleHealthCheck = ({ booking, healthCheckId, onClose, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [healthCheck, setHealthCheck] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState({
    registration: booking?.vehicle?.registration || '',
    make_model: `${booking?.vehicle?.make || ''} ${booking?.vehicle?.model || ''}`,
    year: booking?.vehicle?.year || '',
    fuel_type: booking?.vehicle?.fuel_type || '',
    color: booking?.vehicle?.color || '',
    mileage: booking?.vehicle?.mileage || '',
    vin_number: booking?.vehicle?.vin || '',
    engine_code: '',
    tyre_size: '',
  });

  const [inspectionItems, setInspectionItems] = useState({});
  const [tyreInspections, setTyreInspections] = useState({});
  const [generalNotes, setGeneralNotes] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [requestedItems, setRequestedItems] = useState([{ name: '', quantity: 1, priority: 'medium' }]);
  const [statusCounts, setStatusCounts] = useState({ red: 0, amber: 0, green: 0 });
  const [mediaFiles, setMediaFiles] = useState({});
  const [uploadingMedia, setUploadingMedia] = useState(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const [imageToAnnotate, setImageToAnnotate] = useState(null);
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const autosaveReadyRef = useRef(false);
  const inspectionAutosaveRef = useRef({});
  const tyreAutosaveRef = useRef({});
  const notesAutosaveRef = useRef({ generalNotes: '', adminNotes: '', mileage: '' });
  const requestedItemsAutosaveRef = useRef('[]');
  const autosaveRetryCountRef = useRef(0);
  const savedTimeoutRef = useRef(null);
  const fileInputRefs = useRef({});
  const isCompleted = healthCheck?.status === 'completed';

  const buildInspectionAutosaveMap = (items) => {
    const autosaveMap = {};
    Object.values(items).forEach(categoryItems => {
      Object.values(categoryItems).forEach(item => {
        if (item.id) {
          autosaveMap[item.id] = JSON.stringify({
            status: item.status || 'not_checked',
            recommendations: item.recommendations || '',
            requires_parts: !!item.requires_parts,
            estimated_parts_cost: item.estimated_parts_cost || '',
            estimated_labor_cost: item.estimated_labor_cost || '',
            repair_priority: item.repair_priority || 'medium',
          });
        }
      });
    });
    return autosaveMap;
  };

  const buildTyreAutosaveMap = (tyres) => {
    const autosaveMap = {};
    Object.values(tyres).forEach(tyre => {
      if (tyre.id) {
        autosaveMap[tyre.id] = JSON.stringify({
          status: tyre.status || 'not_checked',
          depth_outer: tyre.depth_outer || '',
          depth_middle: tyre.depth_middle || '',
          depth_inner: tyre.depth_inner || '',
          recommendations: tyre.recommendations || '',
          requires_replacement: !!tyre.requires_replacement,
          estimated_cost: tyre.estimated_cost || '',
        });
      }
    });
    return autosaveMap;
  };

  useEffect(() => {
    if (booking) {
      initializeHealthCheck();
    }
  }, [booking, healthCheckId]);

  // Memoize status counts calculation to avoid recalculating on every render
  const calculatedStatusCounts = useMemo(() => {
    let counts = { red: 0, amber: 0, green: 0 };

    // Count inspection items
    Object.values(inspectionItems).forEach(category => {
      Object.values(category).forEach(item => {
        if (item.status && item.status !== 'not_checked') {
          counts[item.status] = (counts[item.status] || 0) + 1;
        }
      });
    });

    // Count tyre inspections
    Object.values(tyreInspections).forEach(tyre => {
      if (tyre.status && tyre.status !== 'not_checked') {
        counts[tyre.status] = (counts[tyre.status] || 0) + 1;
      }
    });

    return counts;
  }, [inspectionItems, tyreInspections]);

  // Update the status counts state when calculated counts change
  useEffect(() => {
    setStatusCounts(calculatedStatusCounts);
  }, [calculatedStatusCounts]);

  // Build the list of save promises for changed data
  const buildSavePromises = (hcId, latestInspectionMap, latestTyreMap, latestNotesState, latestRequestedItemsJson) => {
    const savePromises = [];

    const changedInspectionItemIds = Object.keys(latestInspectionMap).filter(
      itemId => inspectionAutosaveRef.current[itemId] !== latestInspectionMap[itemId]
    );
    const changedTyreIds = Object.keys(latestTyreMap).filter(
      tyreId => tyreAutosaveRef.current[tyreId] !== latestTyreMap[tyreId]
    );
    const notesChanged =
      notesAutosaveRef.current.generalNotes !== latestNotesState.generalNotes ||
      notesAutosaveRef.current.adminNotes !== latestNotesState.adminNotes ||
      notesAutosaveRef.current.mileage !== latestNotesState.mileage;
    const requestedItemsChanged = requestedItemsAutosaveRef.current !== latestRequestedItemsJson;

    changedInspectionItemIds.forEach(itemId => {
      const item = Object.values(inspectionItems)
        .flatMap(categoryItems => Object.values(categoryItems))
        .find(entry => String(entry.id) === itemId);
      if (!item) return;
      savePromises.push(
        api.put(`/v1/evhc/items/${item.id}/`, {
          status: item.status,
          recommendations: item.recommendations,
          requires_parts: item.requires_parts,
          estimated_parts_cost: item.estimated_parts_cost,
          estimated_labor_cost: item.estimated_labor_cost,
          repair_priority: item.repair_priority
        })
      );
    });

    changedTyreIds.forEach(tyreId => {
      const tyre = Object.values(tyreInspections).find(entry => String(entry.id) === tyreId);
      if (!tyre) return;
      savePromises.push(
        api.put(`/v1/evhc/tyres/${tyre.id}/`, {
          status: tyre.status,
          depth_outer: tyre.depth_outer,
          depth_middle: tyre.depth_middle,
          depth_inner: tyre.depth_inner,
          recommendations: tyre.recommendations,
          requires_replacement: tyre.requires_replacement,
          estimated_cost: tyre.estimated_cost
        })
      );
    });

    if (notesChanged || requestedItemsChanged) {
      const patchData = {};
      if (notesChanged) {
        patchData.general_notes = latestNotesState.generalNotes;
        patchData.admin_notes = latestNotesState.adminNotes;
        patchData.mileage = latestNotesState.mileage;
      }
      if (requestedItemsChanged) {
        const validItems = JSON.parse(latestRequestedItemsJson).filter(item => item.name && item.name.trim());
        patchData.requested_items = validItems;
      }
      savePromises.push(api.patch(`/v1/evhc/${hcId}/`, patchData));
    }

    return { savePromises, changedInspectionItemIds, changedTyreIds, notesChanged, requestedItemsChanged };
  };

  // Flush autosave immediately (no debounce) — used on Close and before Complete
  const flushAutosave = async () => {
    if (!autosaveReadyRef.current || !healthCheck?.id || isCompleted) return;

    const latestInspectionMap = buildInspectionAutosaveMap(inspectionItems);
    const latestTyreMap = buildTyreAutosaveMap(tyreInspections);
    const latestNotesState = { generalNotes, adminNotes, mileage: vehicleInfo.mileage || '' };
    const latestRequestedItemsJson = JSON.stringify(requestedItems);

    const { savePromises, changedInspectionItemIds, changedTyreIds, notesChanged, requestedItemsChanged } =
      buildSavePromises(healthCheck.id, latestInspectionMap, latestTyreMap, latestNotesState, latestRequestedItemsJson);

    if (!savePromises.length) return;

    await Promise.all(savePromises);
    if (changedInspectionItemIds.length) inspectionAutosaveRef.current = latestInspectionMap;
    if (changedTyreIds.length) tyreAutosaveRef.current = latestTyreMap;
    if (notesChanged) notesAutosaveRef.current = latestNotesState;
    if (requestedItemsChanged) requestedItemsAutosaveRef.current = latestRequestedItemsJson;
  };

  // Debounced autosave for inspection/tyre/notes/mileage/requestedItems changes.
  useEffect(() => {
    if (!autosaveReadyRef.current || !healthCheck?.id || isCompleted) return;

    const latestInspectionMap = buildInspectionAutosaveMap(inspectionItems);
    const latestTyreMap = buildTyreAutosaveMap(tyreInspections);
    const latestNotesState = { generalNotes, adminNotes, mileage: vehicleInfo.mileage || '' };
    const latestRequestedItemsJson = JSON.stringify(requestedItems);

    const { savePromises, changedInspectionItemIds, changedTyreIds, notesChanged, requestedItemsChanged } =
      buildSavePromises(healthCheck.id, latestInspectionMap, latestTyreMap, latestNotesState, latestRequestedItemsJson);

    if (!savePromises.length) return;

    const timeoutId = setTimeout(async () => {
      // Clear any existing "saved" timeout
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
      setAutosaveStatus('saving');

      try {
        await Promise.all(savePromises);

        if (changedInspectionItemIds.length) inspectionAutosaveRef.current = latestInspectionMap;
        if (changedTyreIds.length) tyreAutosaveRef.current = latestTyreMap;
        if (notesChanged) notesAutosaveRef.current = latestNotesState;
        if (requestedItemsChanged) requestedItemsAutosaveRef.current = latestRequestedItemsJson;

        autosaveRetryCountRef.current = 0;
        setAutosaveStatus('saved');
        savedTimeoutRef.current = setTimeout(() => setAutosaveStatus('idle'), 3000);
      } catch (error) {
        console.error('Error auto-saving health check:', error);
        autosaveRetryCountRef.current += 1;
        if (autosaveRetryCountRef.current >= 3) {
          setAutosaveStatus('error');
        } else {
          setAutosaveStatus('error');
          // Refs not updated, so next render cycle will retry automatically
        }
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    healthCheck?.id,
    isCompleted,
    inspectionItems,
    tyreInspections,
    generalNotes,
    adminNotes,
    vehicleInfo.mileage,
    requestedItems
  ]);

  const initializeHealthCheck = async () => {
    setLoading(true);
    autosaveReadyRef.current = false;
    try {
      let loadHealthCheckId;

      // If specific health check ID provided, use it directly
      if (healthCheckId) {
        loadHealthCheckId = healthCheckId;
      } else {
        // Otherwise, check if an in-progress health check already exists
        const existingChecks = await api.get(`/v1/bookings/${booking.id}/health-checks/`);
        const inProgressCheck = existingChecks.data.find(hc => hc.status === 'in_progress');

        if (inProgressCheck) {
          // Load existing in-progress health check
          loadHealthCheckId = inProgressCheck.id;
        } else {
          // Create a new health check
          const response = await api.post('/v1/evhc/create/', {
            booking_id: booking.id,
            ...vehicleInfo
          });
          loadHealthCheckId = response.data.id;
        }
      }

      if (loadHealthCheckId) {
        // Fetch the full health check details
        const checkResponse = await api.get(`/v1/evhc/${loadHealthCheckId}/`);
        setHealthCheck(checkResponse.data);

        // Update vehicle info from API response
        if (checkResponse.data.vehicle_info) {
          setVehicleInfo(checkResponse.data.vehicle_info);
        }

        // Set status counts from API
        if (checkResponse.data.status_counts) {
          setStatusCounts(checkResponse.data.status_counts);
        }

        // Initialize inspection items state
        const items = {};
        Object.entries(checkResponse.data.inspection_items).forEach(([category, categoryItems]) => {
          items[category] = {};
          categoryItems.forEach(item => {
            items[category][item.item_name] = {
              id: item.id,
              status: item.status || 'not_checked',
              recommendations: item.recommendations || '',
              requires_parts: item.requires_parts || false,
              estimated_parts_cost: item.estimated_parts_cost || '',
              estimated_labor_cost: item.estimated_labor_cost || '',
              repair_priority: item.repair_priority || 'medium'
            };
          });
        });
        setInspectionItems(items);

        // Initialize tyre inspections state
        const tyres = {};
        checkResponse.data.tyre_inspections.forEach(tyre => {
          tyres[tyre.position] = {
            id: tyre.id,
            status: tyre.status || 'not_checked',
            depth_outer: tyre.depth_outer || '',
            depth_middle: tyre.depth_middle || '',
            depth_inner: tyre.depth_inner || '',
            recommendations: tyre.recommendations || '',
            requires_replacement: tyre.requires_replacement || false,
            estimated_cost: tyre.estimated_cost || ''
          };
        });
        setTyreInspections(tyres);

        // Set general notes
        const initialGeneralNotes = checkResponse.data.general_notes || '';
        setGeneralNotes(initialGeneralNotes);

        // Set admin notes
        const initialAdminNotes = checkResponse.data.admin_notes || '';
        setAdminNotes(initialAdminNotes);

        // Set requested items
        if (checkResponse.data.requested_items && checkResponse.data.requested_items.length > 0) {
          setRequestedItems(checkResponse.data.requested_items);
        } else {
          setRequestedItems([{ name: '', quantity: 1, priority: 'medium' }]);
        }

        // Fetch media files (pass ID directly to avoid race condition with setState)
        await fetchMedia(loadHealthCheckId);

        // Initialize autosave baselines after initial data load.
        inspectionAutosaveRef.current = buildInspectionAutosaveMap(items);
        tyreAutosaveRef.current = buildTyreAutosaveMap(tyres);
        notesAutosaveRef.current = {
          generalNotes: initialGeneralNotes,
          adminNotes: initialAdminNotes,
          mileage: checkResponse.data.vehicle_info?.mileage || '',
        };
        const initialRequestedItems = (checkResponse.data.requested_items && checkResponse.data.requested_items.length > 0)
          ? checkResponse.data.requested_items
          : [{ name: '', quantity: 1, priority: 'medium' }];
        requestedItemsAutosaveRef.current = JSON.stringify(initialRequestedItems);
        autosaveReadyRef.current = true;
      }
    } catch (error) {
      console.error('Error initializing health check:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateInspectionItem = (category, itemName, field, value) => {
    setInspectionItems(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [itemName]: {
          ...prev[category][itemName],
          [field]: value
        }
      }
    }));
  };

  const updateTyreInspection = (position, field, value) => {
    setTyreInspections(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        [field]: value
      }
    }));
    // Note: Auto-selection of status based on tread depth removed.
    // Technicians manually select RED/AMBER/GREEN radio buttons.
  };

  // Media upload handlers
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    uploadMedia(file, 'video');
  };

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // Upload all selected images directly (bulk upload)
    files.forEach(file => {
      uploadMedia(file, 'image');
    });

    // Clear the input so the same files can be selected again if needed
    event.target.value = '';
  };

  // Handle annotation save
  const handleAnnotationSave = async (annotatedBlob) => {
    setAnnotatorOpen(false);

    // If editing an existing image, delete it first (skip confirmation since we're replacing it)
    if (pendingImageFile?.mediaId) {
      await deleteMedia(pendingImageFile.mediaId, 'image', true);
    }

    // Convert blob to file
    const annotatedFile = new File([annotatedBlob], pendingImageFile.name, {
      type: 'image/jpeg',
    });

    // Upload the annotated image
    await uploadMedia(annotatedFile, 'image');

    // Reset states
    setImageToAnnotate(null);
    setPendingImageFile(null);
  };

  const uploadMedia = async (file, mediaType = 'video') => {
    try {
      const uploadKey = mediaType === 'video' ? 'general-null' : 'general-images';
      setUploadingMedia(uploadKey);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', mediaType);

      const response = await api.post(
        `/v1/evhc/${healthCheck.id}/media/upload/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // Refetch all media to get fresh signed URLs
        await fetchMedia(healthCheck.id);
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      alert(`Failed to upload ${mediaType}: ` + (error.response?.data?.error || error.message));
    } finally {
      setUploadingMedia(null);
      // Clear file inputs
      if (fileInputRefs.current['general-video']) {
        fileInputRefs.current['general-video'].value = '';
      }
      if (fileInputRefs.current['general-images']) {
        fileInputRefs.current['general-images'].value = '';
      }
    }
  };

  const deleteMedia = async (mediaId, mediaType = 'video', skipConfirm = false) => {
    if (!skipConfirm) {
      const confirmMsg = mediaType === 'video' ?
        'Are you sure you want to delete this video?' :
        'Are you sure you want to delete this image?';

      if (!window.confirm(confirmMsg)) return;
    }

    try {
      await api.delete(`/v1/evhc/media/${mediaId}/delete/`);

      // Remove from state
      const key = mediaType === 'video' ? 'general-null' : 'general-images';
      setMediaFiles(prev => ({
        ...prev,
        [key]: (prev[key] || []).filter(m => m.id !== mediaId)
      }));
    } catch (error) {
      console.error('Error deleting media:', error);
      alert(`Failed to delete ${mediaType}`);
    }
  };

  const fetchMedia = async (healthCheckIdParam) => {
    const checkId = healthCheckIdParam || healthCheck?.id;
    if (!checkId) return;

    try {
      const response = await api.get(`/v1/evhc/${checkId}/media/`);
      const mediaList = response.data.media;

      // Get general videos (not linked to specific items)
      const generalVideos = mediaList.filter(
        media => !media.inspection_item_id && !media.tyre_inspection_id && media.media_type === 'video'
      );

      // Get general images (not linked to specific items)
      const generalImages = mediaList.filter(
        media => !media.inspection_item_id && !media.tyre_inspection_id && media.media_type === 'image'
      );

      setMediaFiles({
        'general-null': generalVideos,
        'general-images': generalImages
      });
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  };

  const handleClose = async () => {
    try {
      await flushAutosave();
      if (onClose) onClose();
    } catch (error) {
      if (window.confirm('Changes could not be saved. Close anyway?')) {
        if (onClose) onClose();
      }
    }
  };

  const completeHealthCheck = async (createJobRequest = false, completionData = null) => {
    setCompleting(true);
    try {
      // Flush any pending autosave changes first
      await flushAutosave();

      // Now complete the health check
      const validItems = requestedItems.filter(item => item.name.trim() && item.quantity > 0);
      const response = await api.post(`/v1/evhc/${healthCheck.id}/complete/`, {
        create_job_request: createJobRequest,
        general_notes: generalNotes,
        admin_notes: adminNotes,
        requested_items: validItems,
        mileage: vehicleInfo.mileage,
        completion_reasons: completionData?.completion_reasons || [],
        completion_notes: completionData?.completion_notes || '',
      });

      if (response.data.success) {
        if (onClose) {
          onClose();
        }
        if (onComplete) {
          onComplete(response.data);
        }
      }
    } catch (error) {
      console.error('Error completing health check:', error);
      if (onComplete) {
        onComplete({
          success: false,
          message: 'Error completing health check. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setCompleting(false);
    }
  };

  // Handler for completion dialog confirmation
  const handleCompletionConfirm = async (completionData) => {
    setCompletionDialogOpen(false);
    await completeHealthCheck(false, completionData);
  };

  // Requested Items Functions
  const addRequestedItem = () => {
    setRequestedItems([...requestedItems, { name: '', quantity: 1, priority: 'medium' }]);
  };

  const removeRequestedItem = (index) => {
    if (requestedItems.length > 1) {
      const newItems = requestedItems.filter((_, i) => i !== index);
      setRequestedItems(newItems);
    }
  };

  const updateRequestedItem = (index, field, value) => {
    const newItems = [...requestedItems];
    newItems[index][field] = value;
    setRequestedItems(newItems);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'red':
        return <ErrorIcon sx={{ color: '#dc3545' }} />;
      case 'amber':
        return <WarningIcon sx={{ color: '#ffc107' }} />;
      case 'green':
        return <CheckCircleIcon sx={{ color: '#28a745' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'red':
        return '#dc3545';
      case 'amber':
        return '#ffc107';
      case 'green':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!healthCheck) {
    return (
      <Alert severity="error">Failed to initialize health check</Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" gutterBottom>
              Electronic Vehicle Health Check (eVHC)
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Document Number: {healthCheck.document_number}
            </Typography>
            {isCompleted && healthCheck.completed_at && (
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                Completed: {new Date(healthCheck.completed_at).toLocaleString('en-GB')}
              </Typography>
            )}
          </Box>
          {isCompleted && (
            <Chip
              icon={<CheckCircleIcon />}
              label="COMPLETED"
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                px: 2,
                py: 3
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Vehicle Information */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom>
          <CarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Vehicle Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Registration"
              value={vehicleInfo.registration}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Make/Model"
              value={vehicleInfo.make_model}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Year"
              value={vehicleInfo.year}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Fuel Type"
              value={vehicleInfo.fuel_type}
              disabled
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Mileage"
              value={vehicleInfo.mileage}
              onChange={(e) => setVehicleInfo(prev => ({ ...prev, mileage: e.target.value }))}
              size="small"
              type="number"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Status Summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="h6">Inspection Status</Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Chip
                icon={<ErrorIcon />}
                label={`Red: ${statusCounts.red}`}
                sx={{ backgroundColor: '#ffebee', color: '#dc3545' }}
              />
              <Chip
                icon={<WarningIcon />}
                label={`Amber: ${statusCounts.amber}`}
                sx={{ backgroundColor: '#fff3e0', color: '#ff9800' }}
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={`Green: ${statusCounts.green}`}
                sx={{ backgroundColor: '#e8f5e9', color: '#4caf50' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Inspection Items */}
      {Object.entries(inspectionItems).map(([category, items]) => (
        <Accordion key={category} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <BuildIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
              {category}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {Object.entries(items).map(([itemName, item]) => (
                <Paper key={itemName} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <Typography fontWeight="500">
                        {getStatusIcon(item.status)} {itemName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <RadioGroup
                        row
                        value={item.status === 'not_checked' ? 'green' : item.status}
                        onChange={(e) => {
                          if (!isCompleted) {
                            updateInspectionItem(category, itemName, 'status', e.target.value);
                          }
                        }}
                      >
                        <FormControlLabel
                          value="red"
                          control={<Radio disabled={isCompleted} sx={{ color: '#dc3545', '&.Mui-checked': { color: '#dc3545' } }} />}
                          label=""
                        />
                        <FormControlLabel
                          value="amber"
                          control={<Radio disabled={isCompleted} sx={{ color: '#ffc107', '&.Mui-checked': { color: '#ffc107' } }} />}
                          label=""
                        />
                        <FormControlLabel
                          value="green"
                          control={<Radio disabled={isCompleted} sx={{ color: '#28a745', '&.Mui-checked': { color: '#28a745' } }} />}
                          label=""
                        />
                      </RadioGroup>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Recommendations"
                        value={item.recommendations}
                        onChange={(e) => updateInspectionItem(category, itemName, 'recommendations', e.target.value)}
                        size="small"
                        disabled={isCompleted}
                        InputProps={{
                          readOnly: isCompleted
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      {(item.status === 'red' || item.status === 'amber') && (
                        <Box>
                          <FormControlLabel
                            control={
                              <input
                                type="checkbox"
                                checked={item.requires_parts}
                                disabled={isCompleted}
                                onChange={(e) => {
                                  if (!isCompleted) {
                                    updateInspectionItem(category, itemName, 'requires_parts', e.target.checked);
                                  }
                                }}
                              />
                            }
                            label="Needs Parts"
                          />
                        </Box>
                      )}
                    </Grid>

                  </Grid>
                </Paper>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Tyre Inspections */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <CarIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
            Tyre Inspections
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {Object.entries(tyreInspections).map(([position, tyre]) => (
              <Paper key={position} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                    <Typography fontWeight="500">
                      {position.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', minHeight: '56px' }}>
                    <RadioGroup
                      row
                      value={tyre.status === 'not_checked' ? 'green' : tyre.status}
                      onChange={(e) => {
                        if (!isCompleted) {
                          updateTyreInspection(position, 'status', e.target.value);
                        }
                      }}
                      sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                    >
                      <FormControlLabel
                        value="red"
                        control={<Radio disabled={isCompleted} sx={{ color: '#dc3545', '&.Mui-checked': { color: '#dc3545' } }} />}
                        label=""
                      />
                      <FormControlLabel
                        value="amber"
                        control={<Radio disabled={isCompleted} sx={{ color: '#ffc107', '&.Mui-checked': { color: '#ffc107' } }} />}
                        label=""
                      />
                      <FormControlLabel
                        value="green"
                        control={<Radio disabled={isCompleted} sx={{ color: '#28a745', '&.Mui-checked': { color: '#28a745' } }} />}
                        label=""
                      />
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Outer (mm)"
                      value={tyre.depth_outer}
                      onChange={(e) => updateTyreInspection(position, 'depth_outer', e.target.value)}
                      size="small"
                      type="number"
                      inputProps={{ step: 0.1 }}
                      disabled={isCompleted}
                      InputProps={{ readOnly: isCompleted }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Middle (mm)"
                      value={tyre.depth_middle}
                      onChange={(e) => updateTyreInspection(position, 'depth_middle', e.target.value)}
                      size="small"
                      type="number"
                      inputProps={{ step: 0.1 }}
                      disabled={isCompleted}
                      InputProps={{ readOnly: isCompleted }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Inner (mm)"
                      value={tyre.depth_inner}
                      onChange={(e) => updateTyreInspection(position, 'depth_inner', e.target.value)}
                      size="small"
                      type="number"
                      inputProps={{ step: 0.1 }}
                      disabled={isCompleted}
                      InputProps={{ readOnly: isCompleted }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      multiline
                      placeholder="Notes"
                      value={tyre.recommendations}
                      onChange={(e) => updateTyreInspection(position, 'recommendations', e.target.value)}
                      size="small"
                      disabled={isCompleted}
                      InputProps={{ readOnly: isCompleted }}
                    />
                  </Grid>

                </Grid>
              </Paper>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Requested Items */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Requested Items
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item Name/Description</TableCell>
                <TableCell align="center" width="100">Quantity</TableCell>
                <TableCell align="center" width="120">Priority</TableCell>
                <TableCell align="center" width="100">Price</TableCell>
                <TableCell align="center" width="80">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requestedItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      placeholder="e.g., Brake pads, Oil filter, Labor..."
                      value={item.name}
                      onChange={(e) => updateRequestedItem(index, 'name', e.target.value)}
                      variant="outlined"
                      size="small"
                      disabled={isCompleted}
                      InputProps={{ readOnly: isCompleted }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateRequestedItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 1 }}
                      disabled={isCompleted}
                      InputProps={{ readOnly: isCompleted }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={item.priority}
                        onChange={(e) => updateRequestedItem(index, 'priority', e.target.value)}
                        disabled={isCompleted}
                      >
                        <MenuItem value="low">
                          <Box display="flex" alignItems="center">
                            <Box width={8} height={8} borderRadius="50%" bgcolor="#28a745" mr={1} />
                            Low
                          </Box>
                        </MenuItem>
                        <MenuItem value="medium">
                          <Box display="flex" alignItems="center">
                            <Box width={8} height={8} borderRadius="50%" bgcolor="#ffc107" mr={1} />
                            Medium
                          </Box>
                        </MenuItem>
                        <MenuItem value="high">
                          <Box display="flex" alignItems="center">
                            <Box width={8} height={8} borderRadius="50%" bgcolor="#dc3545" mr={1} />
                            High
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      (Admin fills)
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {!isCompleted && (
                      <IconButton
                        size="small"
                        onClick={() => removeRequestedItem(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!isCompleted && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addRequestedItem}
            sx={{ mt: 2 }}
          >
            + ADD ITEM
          </Button>
        )}
      </Paper>

      {/* Notes for Admin */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notes for Admin
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Describe what parts/services you need and any additional context..."
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          variant="outlined"
          disabled={isCompleted}
          InputProps={{ readOnly: isCompleted }}
        />
      </Paper>

      {/* General Notes */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          General Notes & Recommendations
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Add any general notes or recommendations..."
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          variant="outlined"
          disabled={isCompleted}
          InputProps={{ readOnly: isCompleted }}
        />
      </Paper>

      {/* Video Upload Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          <VideocamIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 24 }} />
          Video Documentation
        </Typography>

        {!isCompleted && (mediaFiles['general-null']?.length || 0) === 0 && (
          <Box mb={2}>
            <input
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              ref={el => fileInputRefs.current['general-video'] = el}
              onChange={handleFileSelect}
            />
            <Button
              variant="outlined"
              startIcon={uploadingMedia === 'general-null' ? <CircularProgress size={20} /> : <VideocamIcon />}
              onClick={() => fileInputRefs.current['general-video']?.click()}
              disabled={uploadingMedia === 'general-null'}
            >
              Upload Video
            </Button>
          </Box>
        )}

        {/* Display uploaded videos */}
        {mediaFiles['general-null']?.length > 0 && (
          <Box display="flex" gap={2} flexWrap="wrap">
            {mediaFiles['general-null'].map(media => (
              <Box
                key={media.id}
                position="relative"
                sx={{
                  width: 300,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: '#000'
                }}
              >
                <VideoPlayer
                  media={media}
                  onUrlRefresh={(newUrl) => {
                    // Update the media URL in state
                    setMediaFiles(prev => ({
                      ...prev,
                      'general-null': prev['general-null'].map(m =>
                        m.id === media.id ? { ...m, file_url: newUrl } : m
                      )
                    }));
                  }}
                />
                {!isCompleted && (
                  <IconButton
                    size="small"
                    onClick={() => deleteMedia(media.id)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Image Upload Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          <ImageIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 24 }} />
          Image Documentation
        </Typography>

        {!isCompleted && (
          <Box mb={2}>
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              ref={el => fileInputRefs.current['general-images'] = el}
              onChange={handleImageSelect}
            />
            <Button
              variant="outlined"
              startIcon={uploadingMedia === 'general-images' ? <CircularProgress size={20} /> : <ImageIcon />}
              onClick={() => fileInputRefs.current['general-images']?.click()}
              disabled={uploadingMedia === 'general-images'}
            >
              Upload Images
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              You can select multiple images at once
            </Typography>
          </Box>
        )}

        {/* Display uploaded images */}
        {mediaFiles['general-images']?.length > 0 && (
          <Box display="flex" gap={2} flexWrap="wrap">
            {mediaFiles['general-images'].map(media => (
              <Box
                key={media.id}
                position="relative"
                sx={{
                  width: 200,
                  height: 200,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: '#f5f5f5'
                }}
              >
                <img
                  src={media.file_url.startsWith('http')
                    ? media.file_url
                    : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${media.file_url}`.replace('/api', '')
                  }
                  alt={media.caption || 'Health check image'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (!isCompleted) {
                      // Open annotator for editing
                      const imageUrl = media.file_url.startsWith('http')
                        ? media.file_url
                        : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${media.file_url}`.replace('/api', '');
                      setImageToAnnotate(imageUrl);
                      setPendingImageFile({ name: `annotated-${media.id}.jpg`, mediaId: media.id });
                      setAnnotatorOpen(true);
                    } else {
                      // For completed checks, just view the image
                      setSelectedImage(media.file_url.startsWith('http')
                        ? media.file_url
                        : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${media.file_url}`.replace('/api', '')
                      );
                      setImageViewerOpen(true);
                    }
                  }}
                />
                {!isCompleted && (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => deleteMedia(media.id, 'image')}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255,255,255,0.9)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            ))}
          </Box>
        )}

        {!mediaFiles['general-images']?.length && !uploadingMedia && (
          <Typography variant="body2" color="text.secondary">
            No images uploaded yet
          </Typography>
        )}
      </Paper>

      {/* Actions */}
      {!isCompleted && (
        <Paper sx={{ p: 3, mt: 3 }}>
          {/* Autosave status indicator */}
          {autosaveStatus !== 'idle' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1.5 }}>
              {autosaveStatus === 'saving' && (
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  <SyncIcon sx={{ fontSize: 16, mr: 0.5, animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                  Saving...
                </Typography>
              )}
              {autosaveStatus === 'saved' && (
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                  <CloudDoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  All changes saved
                </Typography>
              )}
              {autosaveStatus === 'error' && (
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
                  <ErrorOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  {autosaveRetryCountRef.current >= 3 ? 'Save failed' : 'Save failed — will retry'}
                </Typography>
              )}
            </Box>
          )}
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={completing}
              >
                Close
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="warning"
                onClick={() => setCompletionDialogOpen(true)}
                disabled={completing}
              >
                Complete Check Only
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={() => completeHealthCheck(true)}
                disabled={completing || (statusCounts.red === 0 && statusCounts.amber === 0 && !(booking?.work_required?.length > 0))}
                startIcon={completing ? <CircularProgress size={20} /> : <AssignmentIcon />}
              >
                Send to Admin
              </Button>
            </Grid>
          </Grid>
          {(statusCounts.red > 0 || statusCounts.amber > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              * Job request will be sent to admin for pricing ({statusCounts.red + statusCounts.amber} items)
            </Typography>
          )}
          {(statusCounts.red === 0 && statusCounts.amber === 0 && booking?.work_required?.length > 0) && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              * No VHC issues found, but services require payment
            </Typography>
          )}
        </Paper>
      )}

      {/* Close button for completed health checks */}
      {isCompleted && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={onClose}
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Image Viewer Modal */}
      <Dialog
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Image Viewer
          <IconButton
            onClick={() => setImageViewerOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
              }}
            >
              <img
                src={selectedImage}
                alt="Full size view"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageViewerOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Annotator Modal */}
      <ImageAnnotator
        open={annotatorOpen}
        onClose={() => {
          setAnnotatorOpen(false);
          setImageToAnnotate(null);
          setPendingImageFile(null);
        }}
        imageUrl={imageToAnnotate}
        onSave={handleAnnotationSave}
      />

      {/* Completion Confirmation Dialog */}
      <CompletionConfirmationDialog
        open={completionDialogOpen}
        onClose={() => setCompletionDialogOpen(false)}
        onConfirm={handleCompletionConfirm}
        bookingInfo={{
          customerName: booking?.customer?.full_name || 'N/A',
          registration: vehicleInfo?.registration || 'N/A',
        }}
        loading={completing}
      />
    </Box>
  );
};

// VideoPlayer component with URL refresh on error
const VideoPlayer = ({ media, onUrlRefresh }) => {
  const [videoUrl, setVideoUrl] = useState(() => {
    return media.file_url.startsWith('http')
      ? media.file_url
      : `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${media.file_url}`.replace('/api', '');
  });
  const [retryCount, setRetryCount] = useState(0);
  const videoRef = useRef(null);

  const refreshVideoUrl = async () => {
    if (retryCount >= 3) {
      console.error('Max retry attempts reached for video:', media.id);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/evhc/media/${media.id}/refresh-url/`);

      if (!response.ok) {
        throw new Error('Failed to refresh video URL');
      }

      const data = await response.json();
      const newUrl = data.file_url.startsWith('http')
        ? data.file_url
        : `${apiUrl}${data.file_url}`.replace('/api', '');

      setVideoUrl(newUrl);
      setRetryCount(prev => prev + 1);

      // Notify parent component
      if (onUrlRefresh) {
        onUrlRefresh(data.file_url);
      }

      // Reload video
      if (videoRef.current) {
        videoRef.current.load();
      }

      console.log('Video URL refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh video URL:', error);
      setRetryCount(prev => prev + 1);
    }
  };

  const handleVideoError = (e) => {
    console.error('Video playback error:', e);
    console.error('Video URL:', videoUrl);
    console.error('Error code:', videoRef.current?.error?.code);

    // Attempt to refresh URL on 403 Forbidden or network errors
    if (videoRef.current?.error?.code === 4 || videoRef.current?.error?.code === 2) {
      console.log('Attempting to refresh video URL...');
      refreshVideoUrl();
    }
  };

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      style={{ width: '100%', display: 'block' }}
      controls
      preload="metadata"
      onError={handleVideoError}
    />
  );
};

export default VehicleHealthCheck;
